/**
 * Plugin del module kind `video` (F3-F) para el CourseModuleRegistry (F2-A).
 *
 * Un módulo `video` referencia un `video_asset` (por `config.videoAssetId`) y lo
 * reproduce como parada de la senda (mismo player que el bloque `video`). Se
 * añade REGISTRANDO este plugin — sin tocar el core del authoring/player (la
 * espina Moodle es extensible). En v1 no aporta a completitud/nota del curso
 * (paralelo a `resource`): el visionado se traza en `video_watch_progress`.
 */

import type { FeatureManifest } from "../academy/country/packs";
import type { ModuleComponentLoaders } from "../academy/modules/plugins";
import {
  type CourseModulePlugin,
  isPlainObject,
  type ModuleValidationResult,
  type SchemaValidator,
} from "../academy/modules/types";

/** Config del módulo `video` (espejo de la referencia al `video_asset`). */
export interface VideoModuleConfig extends Record<string, unknown> {
  /** Id del `video_asset` a reproducir (vacío = aún sin asignar). */
  videoAssetId?: string;
}

const VIDEO_MODULE_MANIFEST: FeatureManifest = {
  key: "academy.module.video",
  name: "Vídeo",
  description:
    "Vídeo bajo demanda (VOD) como parada de la senda: reproducción con reanudación y traza de visionado.",
  scope: "course",
  status: "stable",
};

/** Valida la config del módulo `video`: `videoAssetId` opcional (string). */
export function validateVideoModuleConfig(
  value: unknown,
): ModuleValidationResult<VideoModuleConfig> {
  if (value === null || value === undefined) {
    return { ok: true, value: {} };
  }
  if (!isPlainObject(value)) {
    return { ok: false, errors: ["video module config must be an object"] };
  }
  if (value.videoAssetId !== undefined && typeof value.videoAssetId !== "string") {
    return { ok: false, errors: ["video module `videoAssetId` must be a string"] };
  }
  const cfg: VideoModuleConfig = {};
  if (typeof value.videoAssetId === "string" && value.videoAssetId.trim() !== "") {
    cfg.videoAssetId = value.videoAssetId;
  }
  return { ok: true, value: cfg };
}

const videoModuleConfigSchema: SchemaValidator<VideoModuleConfig> = {
  parse: validateVideoModuleConfig,
};

export function createVideoModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
  // `VideoModuleConfig` es documental: estructuralmente equivale al default
  // (Record<string,unknown>) pero nombra el contrato del config del módulo.
  // oxlint-disable-next-line typescript/no-unnecessary-type-arguments
): CourseModulePlugin<TComponent, VideoModuleConfig> {
  return {
    kind: "video",
    manifest: VIDEO_MODULE_MANIFEST,
    configSchema: videoModuleConfigSchema,
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // v1: no gatea la completitud/nota del curso (el visionado se traza aparte).
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}
